import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { range } from 'lodash';
// @ts-ignore
import Glide from '@glidejs/glide';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import { accentColors, bgColors, colorMap } from '../../utils';

export interface ICarouselProps extends IBaseElementProps {
	showArrows?: boolean;
	showBullets?: boolean;
	perView?: number;
	startAt?: number;
	children?: any;
	fadeColor?: bgColors;
	withDecor?: boolean;
	textColor?: 'light' | 'dark';
	options?: { [x: string]: any };
	onMount?: (carousel: any, ref: HTMLDivElement) => void;
}

// GlideJS extension to disable/enable arrows depending on active carousel item
const ArrowDisabler = (Glide, Components, Events) => ({
	mount() {
		// Only in effect when rewinding is disabled
		if (Glide.settings.rewind) {
			return;
		}

		Glide.on(['mount.after', 'run'], () => {
			// Filter out arrows_control
			for (const controlItem of Components.Controls.items) {
				if (controlItem.className !== 'glide__arrows') {
					continue;
				}

				// Set left arrow state
				const left = controlItem.querySelector('.glide__arrow--left');
				if (left) {
					if (Glide.index === 0) {
						left.setAttribute('disabled', ''); // Disable on first slide
					} else {
						left.removeAttribute('disabled'); // Enable on other slides
					}
				}

				// Set right arrow state
				const right = controlItem.querySelector('.glide__arrow--right');
				if (right) {
					if (Glide.index === Components.Sizes.length - Glide.settings.perView) {
						right.setAttribute('disabled', ''); // Disable on last slide
					} else {
						right.removeAttribute('disabled'); // Disable on other slides
					}
				}
			}
		});
	},
});

/**
 * @requiresHooks
 */

const Carousel: React.FC<ICarouselProps> = ({
	children,
	className,
	classNames = [],
	showArrows = true,
	showBullets = true,
	perView = 1,
	startAt = 0,
	fadeColor,
	withDecor,
	textColor,
	options = {},
	onMount,
}) => {
	const [numSlides, setNumSlides] = useState(0);
	const ref = useRef(null);
	const id = `carousel-${`${Math.random()}`.substr(2)}`;

	const fadeClass = !withDecor
		? `glide--fade-right-${colorMap[fadeColor]}`
		: 'glide--fade-right-light-bg';

	useEffect(() => {
		setTimeout(() => {
			if (!ref.current) {
				return;
			}

			const carousel = ref.current! as HTMLDivElement;

			const totalSlides =
				carousel.querySelectorAll('.glide__slide').length;
			setNumSlides(totalSlides);

			if (carousel.dataset.initialized === 'true' || numSlides < 1) {
				// either already initialized or not ready with numSlides yet.
				return;
			}

			const glide = new Glide(`#${id}`, {
				type: 'carousel',
				startAt,
				perView,
				...options,
			});
			glide.on('move', () => {
				carousel.classList.add('moving');
			});

			glide.on('move.after', () => {
				carousel.classList.remove('moving');
			});

			const mountCarousel = () => {
				if (
					carousel.dataset.initialized === 'true' ||
					totalSlides < 1
				) {
					return;
				}
				carousel.dataset.initialized = 'true';
				glide.mount({ ArrowDisabler });
				onMount && onMount(glide, ref.current!);
			};

			// Initialize inside tabs
			const tabPane = carousel.closest('.tab-pane');
			if (tabPane !== null && !tabPane.classList.contains('active')) {
				const button = document.querySelector(`#${tabPane.id}-tab`);
				button &&
					button!.addEventListener('shown.bs.tab', mountCarousel);
			} else {
				mountCarousel();
			}
		});
	}, [startAt, perView, id, numSlides]);

	return (
		<div
			className={
				className ||
				cn(
					'glide',
					'carousel',
					`carousel--items-${numSlides}`,
					fadeClass,
					textColor && `text-${textColor}`,
					...classNames,
				)
			}
			ref={ref}
			id={id}
		>
			<div className="glide__track" data-glide-el="track">
				<ul className="glide__slides">{children}</ul>
			</div>

			{showArrows && children && (
				<div className="glide__arrows" data-glide-el="controls">
					<button
						className="btn btn-default glide__arrow glide__arrow--left"
						data-glide-dir="<"
						aria-label="Previous"
					>
						<i className="icon icon-chevron-left" />
					</button>
					<button
						className="btn btn-default glide__arrow glide__arrow--right"
						data-glide-dir=">"
						aria-label="Next"
					>
						<i className="icon icon-chevron-right" />
					</button>
				</div>
			)}

			{showBullets && (
				<div className="glide__bullets" data-glide-el="controls[nav]">
					{numSlides > 0 &&
						range(0, numSlides).map((_, index) => (
							<button
								key={index}
								className="glide__bullet"
								data-glide-dir={`=${index}`}
								aria-label={`Go to slide ${index + 1}`}
							/>
						))}
				</div>
			)}
		</div>
	);
};

export default Carousel;
